import clsx from "clsx";
import { useEffect, useRef } from "react";
import { Category } from "services-hooks/types";
import styles from "./category-list.module.scss";
import Image from "next/image";
import MemoLogo from "../icons/Logo";
import ArrowRight from "../icons/ArrowRight";
import { useIntersection } from "react-use";
import { calculateCategoryScroll } from "app/utils/scroll";
import { useTranslate } from "app/hooks/lang";
import { useFeaturesContext } from "app/context/features.context";
import Link from "next/link";

export interface CategoryListProps {
  items: Category[];
  label: string;
  loading: boolean;
  isPremium?: boolean;
  fetchNextPage?: VoidFunction;
}
export const CategoryList: React.FC<CategoryListProps> = ({
  items,
  label,
  loading,
  isPremium = false,
  fetchNextPage = () => {},
}) => {
  const { arrowNavigationEnabled } = useFeaturesContext();
  const t = useTranslate();
  const elementsRef = useRef<HTMLAnchorElement[]>([]);
  const radioListRef = useRef<HTMLDivElement>(null);
  const onForward = () => {
    radioListRef.current?.scrollTo({
      left: calculateCategoryScroll(radioListRef.current.scrollLeft),
      behavior: "smooth",
    });
  };
  const onBackward = () => {
    radioListRef.current?.scrollTo({
      left: calculateCategoryScroll(radioListRef.current.scrollLeft, true),
      behavior: "smooth",
    });
  };
  const loadMoreTriggerRef = useRef(null);
  const intersection = useIntersection(loadMoreTriggerRef, {
    rootMargin: "0px",
    threshold: 1,
  });
  useEffect(() => {
    if (intersection?.isIntersecting) {
      fetchNextPage();
    }
  }, [intersection]);
  return (
    <div className={styles.wrapper}>
      <h2 className={styles.header}>
        <span>
          {label}
          {isPremium && (
            <em className={styles.premium_label}>{t("premium.premium")}</em>
          )}
        </span>

        {!arrowNavigationEnabled && (
          <div className={styles.buttons}>
            <button
              className={clsx(styles.arrow, styles.arrow_back)}
              onClick={() => onBackward()}
            >
              <ArrowRight />
            </button>
            <button className={styles.arrow} onClick={() => onForward()}>
              <ArrowRight />
            </button>
          </div>
        )}
      </h2>
      <div
        className={styles.category_list}
        data-navigate-row
        data-navigate-scrollable
        ref={radioListRef}
      >
        {loading ? (
          <>
            <div className={clsx(styles.category, styles.placeholder_item)} />
            <div className={clsx(styles.category, styles.placeholder_item)} />
            <div className={clsx(styles.category, styles.placeholder_item)} />
            <div className={clsx(styles.category, styles.placeholder_item)} />
            <div className={clsx(styles.category, styles.placeholder_item)} />
            <div className={clsx(styles.category, styles.placeholder_item)} />
          </>
        ) : (
          <>
            {items.map((category, index) => (
              <Link
                key={`category-item-${category.permalink}`}
                href={`/category/${category.permalink!}`}
              >
                <a
                  className={styles.category}
                  tabIndex={0}
                  data-navigate-item
                  key={`category-${index}`}
                  ref={(e) => {
                    if (e) {
                      elementsRef.current[index] = e;
                    }
                  }}
                >
                  <div className={styles.category_img}>
                    {category.logo ? (
                      <Image
                        className={styles.cover}
                        key={`radio-station-${index}`}
                        src={category.logo}
                        width={220}
                        height={116}
                        layout="fixed"
                        objectFit="cover"
                        alt={category.name}
                        unoptimized
                      />
                    ) : (
                      <div className={styles.cover_empty}>
                        <MemoLogo />
                      </div>
                    )}
                  </div>
                  <div className={styles.category_details}>
                    <h3 className={styles.category_name}>{category.name}</h3>
                    <div className={styles.category_description}>
                      {category.description}
                    </div>
                  </div>
                </a>
              </Link>
            ))}
            <div ref={loadMoreTriggerRef} />
          </>
        )}
      </div>
    </div>
  );
};
